/**
 * ReviewFlagCard — what the system flagged, and how it was settled (BON-381).
 *
 * The design prototype headed this card with a per-reason LABEL and followed it
 * with a per-reason blurb, both drawn from a code enum. Our reason is free text
 * written by the payment path, so the heading is the state ("Flagged for
 * review") and the prose sits in the reason block where it belongs — nothing is
 * looked up, and no severity is inferred.
 *
 * `flaggedBy` is "System": no flagger identity is stored anywhere, and inventing
 * one ("Reconciliation job") would be a lie on an audit surface.
 */
import { Avatar } from '../dashboards/primitives';

import {
  ageLabel,
  formatInstant,
  latestResolutionNote,
  reasonClauses,
  reasonText,
  resolvedStamp,
} from './reviewQueueModel';
import type { ReviewDetail, ReviewState } from './reviewTypes';

export interface ReviewFlagCardProps {
  detail: ReviewDetail;
}

const LABEL_STYLE = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: '.05em',
  textTransform: 'uppercase',
  color: 'var(--nb-subtle)',
  marginBottom: 5,
} as const;

/**
 * The status pill — one label per state, straight off `detail.state`.
 *
 * An item stops needing review in two ways and they are NOT the same event: an
 * operator resolved it, or the system handed the flag to the booking created for
 * the checkout (`superseded`, no actor, no stamp). Calling the second one
 * "Resolved" credits a person who did nothing.
 *
 * This used to reason it out from `needsReview`, then the presence of a stamp,
 * then the last history action, with a "Closed" fallback for the case none of
 * those covered. Every one of those was a re-derivation of a state the API
 * already knows, and the fallback was where a genuinely resolved item landed
 * whenever any link in the chain disagreed. Exhaustive switch, no fallback: a
 * fourth state cannot be added without the compiler stopping here.
 */
function statusPill(state: ReviewState): { label: string; done: boolean } {
  switch (state) {
    case 'open':
      return { label: 'Needs review', done: false };
    case 'resolved':
      return { label: 'Resolved', done: true };
    case 'superseded':
      return { label: 'Superseded', done: true };
  }
}

/** Every clause, one per line — the heading shows the first, and a row flagged
 *  for three reasons needs all three somewhere legible. A list when there is more
 *  than one, because that is what it is. */
function ReasonBlock({ clauses }: { clauses: string[] }) {
  return (
    <div style={{ background: 'var(--nb-surface)', borderRadius: 10, padding: '11px 13px' }}>
      <div style={LABEL_STYLE}>
        {clauses.length > 1 ? `Review reasons (${clauses.length})` : 'Review reason'}
      </div>
      {clauses.length > 1 ? (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.5 }}>
          {clauses.map((clause) => (
            <li key={clause} style={{ color: 'var(--nb-ink-2)' }}>
              {clause}
            </li>
          ))}
        </ul>
      ) : (
        <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--nb-ink-2)' }}>{clauses[0]}</div>
      )}
    </div>
  );
}

export function ReviewFlagCard({ detail }: ReviewFlagCardProps) {
  const stamp = resolvedStamp(detail);
  const clauses = reasonClauses(detail.reviewReason);
  const status = statusPill(detail.state);
  const note = latestResolutionNote(detail.history);

  return (
    <section className="nb-card" style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          {/* U11: the REASON is the headline. This h2 read "Flagged for review"
              beside a pill reading "Needs review" — two ways of saying the same
              nothing — while the one fact the operator opened the page for sat at
              13px in grey below. Worse, on a resolved item the h2 asserted the
              flag was live and the pill said it was not.
              First clause only: several reasons join with "; " and a heading is
              not the place for a paragraph. The whole string stays in the Review
              reason block below, and on the title. */}
          <h2
            className="nb-display"
            style={{ margin: '0 0 4px', fontSize: 19, color: 'var(--nb-bark-dark)' }}
            title={reasonText(detail.reviewReason)}
          >
            {reasonClauses(detail.reviewReason)[0]}
          </h2>
          <div style={{ fontSize: 12, color: 'var(--nb-muted)' }}>
            Flagged {formatInstant(detail.flaggedAt)} by System · {ageLabel(detail.ageDays)} old
          </div>
        </div>
        <span
          className="nb-pill"
          style={{
            height: 22,
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: '.05em',
            textTransform: 'uppercase',
            border: 0,
            color: status.done ? 'var(--nb-primary-2)' : 'var(--nb-warn)',
            background: status.done ? 'var(--nb-primary-soft)' : 'var(--nb-warn-soft)',
          }}
        >
          {status.label}
        </span>
      </div>

      <ReasonBlock clauses={clauses} />

      {stamp && (
        <div
          style={{
            background: 'var(--nb-primary-soft)',
            borderRadius: 10,
            padding: '11px 13px',
            display: 'flex',
            gap: 10,
          }}
        >
          <Avatar name={stamp.name} size={28} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--nb-primary-2)' }}>
              Resolved by {stamp.name} · {stamp.at}
            </div>
            {note && (
              <div
                style={{
                  fontSize: 12.5,
                  color: 'var(--nb-ink-2)',
                  marginTop: 3,
                  lineHeight: 1.5,
                }}
              >
                {note}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
